import { FunctionComponent, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, ClickAwayListener, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import clsx from 'clsx';
import { useFieldStyles } from 'components/UI/FieldStyles';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons';
import { Option } from '@spike/model';
import { PetServiceWrapper } from 'model/NewMultipleBooking';
import { WalkingService, TrainingService } from 'model/Service';
export interface DurationSelectProps {
    id?: string;
    label?: string | JSX.Element;
    name?: string;
    selectedOption?: PetServiceWrapper | WalkingService | TrainingService;
    placeholder?: string;
    durationToAdd?: any;
    disabled?: boolean;
    className?: string;
    type: 'pet' | 'walking' | 'training';
    onSelect: (option: PetServiceWrapper | WalkingService | TrainingService, data: boolean) => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            minWidth: 110
        },
        dropdownContainer: {
            position: 'relative',
            height: 54
        },
        collapsedContainer: {
            height: '100%'
        },
        expandedContainer: {
            display: 'flex',
            flexDirection: 'column',
            position: 'absolute',
            top: 0,
            left: 0,
            zIndex: 10,
            width: '100%',
            backgroundColor: 'white',
            border: '1px solid #222222 !important',
            paddingBottom: 30,
            [theme.breakpoints.down('md')]: {
                paddingBottom: 33
            }
        },
        headerContainer: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            height: 49,
            cursor: 'pointer'
        },
        header: {
            display: 'flex',
            alignItems: 'center',
            color: '#000000',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            fontSize: '16px',
            lineHeight: '27px',
            paddingLeft: 28,
            width: 290,
            [theme.breakpoints.up('md')]: {
                paddingLeft: 30,
                width: 420
            }
        },
        listContainer: {
            borderTop: '1px solid #222222',
            overflowY: 'auto',
            height: 'auto',
            maxHeight: 200,
            paddingTop: 12,
            [theme.breakpoints.up('md')]: {
                paddingTop: 14
            }
        },
        text: {
            color: '#000000',
            fontSize: 16,
            lineHeight: '27px',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            overflow: 'hidden'
        },
        placeholder: {
            color: '#7A7A7A',
            paddingLeft: 18,
            [theme.breakpoints.up('md')]: {
                paddingLeft: 20
            }
        },
        border: {
            border: '1px solid #222222',
            borderRadius: 27,
            [theme.breakpoints.up('md')]: {
                borderRadius: 30
            }
        },
        textContainer: {
            alignItems: 'center',
            paddingRight: 10,
            width: '85%'
        },
        chevronContainer: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            paddingRight: 32,
            [theme.breakpoints.up('md')]: {
                paddingRight: 35
            }
        },
        itemContainer: {
            'display': 'flex',
            'flexDirection': 'column',
            'justifyContent': 'center',
            'height': 45,
            '&:hover': {
                background: '#F8F8F8'
            }
        },
        itemContent: {
            display: 'flex',
            width: '100%',
            paddingTop: 0,
            paddingBottom: 0,
            paddingLeft: 20,
            paddingRight: 20,
            cursor: 'pointer'
        },
        itemContentSelected: {
            paddingLeft: 18,
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            alignItems: 'center',
            marginRight: 4,
            width: '95%',
            [theme.breakpoints.up('md')]: {
                paddingLeft: 20
            }
        },
        item: {
            fontSize: 16,
            lineHeight: '16px'
        },
        optionNotSelected: {
            border: '1px solid #D3D3D3 !important'
        },
        chevron: {
            fontSize: 16
        },
        bold: {
            fontWeight: 500
        },
        sectionDuration: {
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            alignItems: 'center',
            marginRight: 4,
            height: 17,
            width: '80%',
            [theme.breakpoints.up('md')]: {
                marginRight: 5
            }
        },
        durationNameContainer: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            width: '100%'
        },
        title: {
            fontWeight: 600,
            fontSize: 16,
            lineHeight: '16px',
            marginBottom: 18,
            [theme.breakpoints.up('md')]: {
                marginBottom: 20
            }
        }
    })
);

export const DurationSelect: FunctionComponent<DurationSelectProps> = props => {
    const classes = useStyles();
    const fieldClasses = useFieldStyles();

    const options: Array<Option<number>> = [
        { id: 5, name: '5 min' },
        { id: 10, name: '10 min' },
        { id: 15, name: '15 min' },
        { id: 30, name: '30 min' },
        { id: 45, name: '45 min' },
        { id: 60, name: '1 h' },
        { id: 65, name: '1 h 5 min' },
        { id: 70, name: '1 h 10 min' },
        { id: 75, name: '1 h 15 min' },
        { id: 80, name: '1 h 20 min' },
        { id: 85, name: '1 h 25 min' },
        { id: 90, name: '1 h 30 min' },
        { id: 95, name: '1 h 35 min' },
        { id: 100, name: '1 h 40 min' },
        { id: 105, name: '1 h 45 min' },
        { id: 110, name: '1 h 50 min' },
        { id: 115, name: '1 h 55 min' },
        { id: 120, name: '2 h' },
        { id: 135, name: '2 h 15 min' },
        { id: 150, name: '2 h 30 min' },
        { id: 165, name: '2 h 45 min' },
        { id: 180, name: '3 h' },
        { id: 195, name: '3 h 15 min' },
        { id: 210, name: '3 h 30 min' },
        { id: 225, name: '3 h 45 min' },
        { id: 240, name: '4 h' },
        { id: 255, name: '4 h 15 min' },
        { id: 270, name: '4 h 30 min' },
        { id: 285, name: '4 h 45 min' },
        { id: 300, name: '5 h' },
        { id: 315, name: '5 h 15 min' },
        { id: 330, name: '5 h 30 min' },
        { id: 345, name: '5 h 45 min' },
        { id: 360, name: '6 h' },
        { id: 375, name: '6 h 15 min' },
        { id: 390, name: '6 h 30 min' },
        { id: 405, name: '6 h 45 min' },
        { id: 420, name: '7 h' },
        { id: 435, name: '7 h 15 min' },
        { id: 450, name: '7 h 30 min' },
        { id: 465, name: '7 h 45 min' },
        { id: 480, name: '8 h' },
        { id: 540, name: '9 h' },
        { id: 600, name: '10 h' },
        { id: 660, name: '11 h' },
        { id: 720, name: '12 h' }
    ];

    const [isCollapsed, setIsCollapsed] = useState(true);
    const [optionsState, setOptionsState] = useState<Array<Option<number>>>(options);

    const clickCollapseHandler = () => {
        setIsCollapsed(prev => !prev);
    };

    const changeHandler = (idSelected: number) => {
        if (props.selectedOption) {
            const durationInMinutes = optionsState.find(option => option.id === idSelected)!.id;
            let optionSelected;
            if (props.type === 'walking') {
                optionSelected = {
                    ...props.selectedOption,
                    duration: durationInMinutes
                } as WalkingService;
                props.onSelect(optionSelected as WalkingService, true);
            } else if (props.type === 'training') {
                optionSelected = {
                    ...props.selectedOption,
                    duration: durationInMinutes
                } as TrainingService;
                props.onSelect(optionSelected as TrainingService, true);
            } else {
                optionSelected = {
                    ...props.selectedOption,
                    durationInMinutes: durationInMinutes,
                    duration: {
                        hours: Math.floor(durationInMinutes / 60),
                        minutes: durationInMinutes % 60
                    }
                } as PetServiceWrapper;
                props.onSelect(optionSelected as PetServiceWrapper, true);
            }
        }
    };

    const getDuration = () => {
        const durationPreselected = optionsState.find(
            (opt: Option<number>) =>
                opt.id ===
                (props.type === 'walking'
                    ? (props.selectedOption as WalkingService)?.duration
                    : props.type === 'training'
                    ? (props.selectedOption as TrainingService)?.duration
                    : (props.selectedOption as PetServiceWrapper)?.durationInMinutes)
        );

        if (durationPreselected) {
            return durationPreselected;
        }
    };

    useEffect(() => {
        if (props.durationToAdd) {
            const optionsCopy = [...options];
            const optionToAdd = options.find(option => option.id === props.durationToAdd.id);
            if (!optionToAdd) {
                optionsCopy.push(props.durationToAdd);
                optionsCopy.sort((duration, otherDuration) => {
                    if (duration.id > otherDuration.id) {
                        return 1;
                    } else {
                        return -1;
                    }
                });
                setOptionsState(optionsCopy);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.durationToAdd]);

    const header = (
        <Box className={classes.headerContainer}>
            <Box className={classes.textContainer}>
                {props.selectedOption ? (
                    <Box className={clsx(classes.itemContent, classes.itemContentSelected, classes.text)}>
                        <Typography className={classes.text}>{getDuration()?.name}</Typography>
                    </Box>
                ) : (
                    <Typography className={clsx(classes.placeholder, classes.text)}>{props.placeholder}</Typography>
                )}
            </Box>
            <Box className={classes.chevronContainer}>
                <FontAwesomeIcon
                    icon={isCollapsed ? faChevronDown : faChevronUp}
                    color={isCollapsed ? '#7A7A7A' : '#222222'}
                    className={classes.chevron}
                />
            </Box>
        </Box>
    );

    const list = (
        <Box className={classes.listContainer}>
            {optionsState.map((opt, index) => (
                <Box
                    className={classes.itemContainer}
                    key={opt.id}
                    id={`booking_option_duration_${index}`}
                    onClick={() => changeHandler(opt.id!)}
                >
                    <Box className={classes.itemContent}>
                        <Box className={classes.durationNameContainer}>
                            <Typography className={clsx(classes.item, classes.sectionDuration)}>{opt.name}</Typography>
                        </Box>
                    </Box>
                </Box>
            ))}
        </Box>
    );

    const collapsed = (
        <Box
            className={clsx(classes.border, classes.collapsedContainer, [classes.optionNotSelected])}
            onClick={clickCollapseHandler}
        >
            {header}
        </Box>
    );

    const expanded = (
        <Box
            className={clsx(classes.border, classes.expandedContainer)}
            onClick={clickCollapseHandler}
        >
            {header}
            {list}
        </Box>
    );

    return (
        <ClickAwayListener onClickAway={() => setIsCollapsed(true)}>
            <Box className={clsx(classes.container, fieldClasses.container, props.className)}>
                <Typography className={classes.title}>{props.label}</Typography>
                <Box
                    className={classes.dropdownContainer}
                    id="booking_list_durations"
                >
                    {isCollapsed ? collapsed : expanded}
                </Box>
            </Box>
        </ClickAwayListener>
    );
};

export default DurationSelect;
